import { styled, alpha, type Theme } from '@mui/material/styles';
import { getTableRowHoverOverlay } from '../../../../styles/theme/tableRowOverlays';

/** Estilos MUI Select cuando el flag está apagado (valor heredado, solo lectura). */
export const inheritedSelectSx = (theme: Theme) => ({
  '&.Mui-disabled': {
    opacity: 1,
  },
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : theme.palette.background.paper,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '& .MuiSelect-select.Mui-disabled': {
    color: theme.palette.text.disabled,
    WebkitTextFillColor: theme.palette.text.disabled,
  },
});

// Container styles
export const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}));

export const EmptyState = styled('div')(({ theme }) => ({
  textAlign: 'center',
  padding: '32px 0',
}));

export const EmptyStateText = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

// Summary info styles
export const SummaryInfo = styled('div')(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.12) : '#eff6ff',
  border:
    theme.palette.mode === 'dark'
      ? `1px solid ${alpha(theme.palette.primary.main, 0.35)}`
      : '1px solid #bfdbfe',
  borderRadius: '8px',
  padding: '16px',
}));

export const SummaryTitle = styled('h3')(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#1e40af',
  marginBottom: '8px',
}));

export const SummaryDescription = styled('p')(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#1d4ed8',
  fontSize: '14px',
}));

export const SummaryStats = styled('p')(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2563eb',
  fontSize: '12px',
  marginTop: '4px',
}));

// Tabs styles
export const TabsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: '24px',
}));

export const TabButton = styled('button')<{ active: boolean }>(({ theme, active }) => ({
  padding: '12px 24px',
  fontWeight: 500,
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '8px 8px 0 0',
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.2s',
  ...(active
    ? {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#0891b2',
        borderTop: `1px solid ${theme.palette.divider}`,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      }
    : {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : '#f3f4f6',
        color: theme.palette.text.secondary,
        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.action.selected : '#e5e7eb',
        },
      }),
}));

export const TabIcon = styled('span')(({ theme }) => ({
  marginRight: '8px',
}));

// Product groups container
export const ProductGroupsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
}));

// Product card styles
export const ProductCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
}));

export const ProductHeader = styled('div')<{ gradientType: 'blue' | 'green' }>(
  ({ theme, gradientType }) => ({
    padding: '24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 0.9,
    },
    ...(gradientType === 'blue'
      ? {
          background:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.12)
              : 'linear-gradient(to right, #eff6ff, #ecfeff)',
        }
      : {
          background:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.success.main, 0.12)
              : 'linear-gradient(to right, #f0fdf4, #ecfdf5)',
        }),
  }),
);

export const ProductHeaderContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const ProductInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

export const ProductIcon = styled('span')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    marginRight: '12px',
    ...(colorType === 'blue'
      ? {
          color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2563eb',
        }
      : {
          color: theme.palette.mode === 'dark' ? theme.palette.success.light : '#059669',
        }),
  }),
);

export const ProductDetails = styled('div')(({ theme }) => ({}));

export const ProductName = styled('h3')(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '18px',
}));

export const ProductCode = styled('p')(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
}));

export const ProductActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

export const ResetButton = styled('button')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    marginRight: '16px',
    padding: '4px 12px',
    fontSize: '12px',
    borderRadius: '6px',
    border: '1px solid',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ...(colorType === 'blue'
      ? theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.primary.main, 0.55),
            color: theme.palette.primary.light,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
          }
        : {
            borderColor: '#93c5fd',
            color: '#1d4ed8',
            '&:hover': {
              backgroundColor: '#dbeafe',
            },
          }
      : theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.success.main, 0.55),
            color: theme.palette.success.light,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: alpha(theme.palette.success.main, 0.18),
            },
          }
        : {
            borderColor: '#6ee7b7',
            color: '#047857',
            '&:hover': {
              backgroundColor: '#d1fae5',
            },
          }),
  }),
);

export const BulkEditIndicator = styled('span')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    fontSize: '14px',
    fontWeight: 500,
    marginRight: '16px',
    ...(colorType === 'blue'
      ? {
          color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#1d4ed8',
        }
      : {
          color: theme.palette.mode === 'dark' ? theme.palette.success.light : '#047857',
        }),
  }),
);

export const UnitPaginationBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginRight: '12px',
}));

export const UnitPaginationLabel = styled('span')(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  minWidth: '72px',
  textAlign: 'center',
}));

export const UnitPaginationButton = styled('button')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    width: '28px',
    height: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    border: '1px solid',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: 1,
    padding: 0,
    transition: 'all 0.2s',
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
    ...(colorType === 'blue'
      ? theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.primary.main, 0.55),
            color: theme.palette.primary.light,
            backgroundColor: 'transparent',
            '&:hover:not(:disabled)': {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
          }
        : {
            borderColor: '#93c5fd',
            color: '#1d4ed8',
            backgroundColor: '#fff',
            '&:hover:not(:disabled)': {
              backgroundColor: '#dbeafe',
            },
          }
      : theme.palette.mode === 'dark'
        ? {
            borderColor: alpha(theme.palette.success.main, 0.55),
            color: theme.palette.success.light,
            backgroundColor: 'transparent',
            '&:hover:not(:disabled)': {
              backgroundColor: alpha(theme.palette.success.main, 0.18),
            },
          }
        : {
            borderColor: '#6ee7b7',
            color: '#047857',
            backgroundColor: '#fff',
            '&:hover:not(:disabled)': {
              backgroundColor: '#d1fae5',
            },
          }),
  }),
);

export const ExpandIcon = styled('span')<{ colorType: 'blue' | 'green' }>(
  ({ theme, colorType }) => ({
    ...(colorType === 'blue'
      ? {
          color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2563eb',
        }
      : {
          color: theme.palette.mode === 'dark' ? theme.palette.success.light : '#059669',
        }),
  }),
);

// Table styles
export const TableContainer = styled('div')(({ theme }) => ({
  overflowX: 'auto',
}));

export const Table = styled('table')(({ theme }) => ({
  tableLayout: 'fixed',
  minWidth: '720px',
  width: '100%',
}));

export const TableHead = styled('thead')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

export const TableRow = styled('tr')(({ theme }) => ({
  '&:last-child td': {
    borderBottom: 0,
  },
  transition: 'background 0.2s',
  '&:hover': {
    backgroundColor: `${getTableRowHoverOverlay(theme)} !important`,
    background: `${getTableRowHoverOverlay(theme)} !important`,
  },
}));

export const HeaderCell = styled('th')(({ theme }) => ({
  padding: `${theme.spacing(0.8)} ${theme.spacing(1)}`,
  textAlign: 'left',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: theme.palette.text.secondary || '#6b7280',
  textTransform: 'capitalize',
  letterSpacing: 0.8,
  borderBottom: `1px solid ${theme.palette.divider || '#e5e7eb'}`,
  whiteSpace: 'nowrap',
  backgroundColor: theme.palette.background.paper,
}));

export const HeaderCellWithActions = styled(HeaderCell)(({ theme }) => ({
  textAlign: 'center',
  '& > div': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
}));

export const ActionButton = styled('button')(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  transition: 'color 0.2s',
  '&:hover': {
    color: theme.palette.primary.light,
  },
  '&:not(:last-child)': {
    marginLeft: '4px',
  },
}));

export const TableBody = styled('tbody')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '& > tr': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: 'background 0.2s',
    '&:hover': {
      backgroundColor: `${getTableRowHoverOverlay(theme)} !important`,
      background: `${getTableRowHoverOverlay(theme)} !important`,
    },
  },
}));

export const BodyCell = styled('td')(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  whiteSpace: 'nowrap',
  fontSize: '1rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const InputField = styled('input')(({ theme }) => ({
  width: '100%',
  padding: '8px 12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '6px',
  fontSize: '14px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.background.paper,
  color: theme.palette.text.primary,
  '&:focus': {
    outline: 'none',
    ring: '2px',
    ringColor: '#0891b2',
    borderColor: '#0891b2',
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
    WebkitTextFillColor: theme.palette.text.disabled,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : theme.palette.background.paper,
    borderColor: theme.palette.divider,
    cursor: 'not-allowed',
    opacity: 1,
  },
}));

export const SelectField = styled('div')(({ theme }) => ({
  width: '100%',
}));

// Bulk edit panel styles
export const BulkEditPanel = styled('div')<{ activeTab: 'properties' | 'costs' }>(
  ({ theme, activeTab }) => ({
    padding: '16px',
    borderTop: '1px solid',
    borderRadius: '8px',
    ...(activeTab === 'properties'
      ? {
          backgroundColor: '#ecfeff',
          borderColor: '#a7f3d0',
        }
      : {
          backgroundColor: '#f0fdf4',
          borderColor: '#a7f3d0',
        }),
  }),
);

export const BulkEditContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
}));

export const BulkEditLabel = styled('span')<{ activeTab: 'properties' | 'costs' }>(
  ({ theme, activeTab }) => ({
    fontWeight: 500,
    marginRight: '12px',
    ...(activeTab === 'properties'
      ? {
          color: '#155e75',
        }
      : {
          color: '#14532d',
        }),
  }),
);

export const BulkEditInputContainer = styled('div')(({ theme }) => ({
  flexGrow: 1,
  maxWidth: '384px',
  display: 'flex',
}));

export const BulkEditInput = styled('input')<{ activeTab: 'properties' | 'costs' }>(
  ({ theme, activeTab }) => ({
    flexGrow: 1,
    padding: '8px 12px',
    border: '1px solid',
    borderBottom: '1px solid',
    borderRadius: '6px 0 0 6px',
    fontSize: '14px',
    '&:focus': {
      outline: 'none',
      ring: '2px',
      borderBottom: '1px solid',
      ...(activeTab === 'properties'
        ? {
            ringColor: '#0891b2',
            borderColor: '#0891b2',
          }
        : {
            ringColor: '#10b981',
            borderColor: '#10b981',
          }),
    },
    '&::after': {
      display: 'none',
    },
    '&::before': {
      display: 'none',
    },
    ...(activeTab === 'properties'
      ? {
          borderColor: '#67e8f9',
        }
      : {
          borderColor: '#6ee7b7',
        }),
  }),
);

export const BulkEditSelect = styled('div')<{ activeTab: 'properties' | 'costs' }>(
  ({ theme, activeTab }) => ({
    flexGrow: 1,
    '& .MuiSelect-root': {
      borderRadius: '6px 0 0 6px',
      ...(activeTab === 'properties'
        ? {
            borderColor: '#67e8f9',
            '&:focus': {
              ringColor: '#0891b2',
              borderColor: '#0891b2',
            },
          }
        : {
            borderColor: '#6ee7b7',
            '&:focus': {
              ringColor: '#10b981',
              borderColor: '#10b981',
            },
          }),
    },
  }),
);

export const BulkEditApplyButton = styled('button')(({ theme }) => ({
  padding: '8px 16px',
  background: 'linear-gradient(to right, #0891b2, #10b981)',
  color: '#ffffff',
  borderRadius: '0 6px 6px 0',
  border: 'none',
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 0.9,
  },
}));

export const BulkEditCancelButton = styled('button')(({ theme }) => ({
  marginLeft: '12px',
  padding: '8px 12px',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: '6px',
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));
